import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import * as turf from '@turf/turf';

import {
  UserProject,
  UserProjectRequest,
  UserProjectSubscription,
} from 'state/userAdminSlices';

import { MapContext, MapContextProvider } from 'features/map/MapContext';
import {
  addMapLayerInDefinedStack,
  initMap,
  mapLayerSafeRemove,
  mapSourceSafeRemove,
} from 'features/map/utils/mapCommon';
import {
  getSubscriptionProjectsFillLayerDefinition,
  getSubscriptionProjectsLabelLayerDefinition,
  getSubscriptionProjectsLineLayerDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { fitBoundsToBbox } from 'features/map/utils/mapUtils';
import {
  initZoom,
  kSubscriptionProjectFillLayerId,
  kSubscriptionProjectLabelsLayerId,
  kSubscriptionProjectLabelsSourceId,
  kSubscriptionProjectLineLayerId,
  kSubscriptionProjectSourceId,
  safeCenter,
} from 'features/map/utils/map_constants';
import { useMapSubscriptionBoundary } from 'features/user_admin/adminMapCommon';
import { projectUrl } from 'features/user_admin/common';
import { Feature, Point, Polygon } from 'geojson';

import styles from 'features/user_admin/NewUserProjectRequestPage.module.css';

export function SubscriptionProjectsMapInner({
  subscription,
}: {
  subscription: UserProjectSubscription;
}) {
  const navigate = useNavigate();
  const refMapContainer = useRef(null); // DOM node reference
  const { refMap, mapReady, setMapReady, refStyleReady, mapStyle } =
    useContext(MapContext);

  const subscriptionBoundary = useMemo(() => {
    return (
      subscription?.template?.region_boundary &&
      turf.feature(subscription?.template?.region_boundary)
    );
  }, [subscription]);
  useMapSubscriptionBoundary(subscriptionBoundary, undefined);

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  useEffect(() => {
    const unmount = () => {
      // console.log('Unmount map');
      setMapReady(false);
      refStyleReady.current = false;
      // Wipe old map
      if (refMap.current) {
        refMap.current.remove();
        refMap.current = undefined;
      }
    };
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
    }

    const center = { ...safeCenter };
    if (subscriptionBoundary) {
      const sub_center = turf.centroid(subscriptionBoundary);
      // eslint-disable-next-line prefer-destructuring
      center.lat = sub_center.geometry.coordinates[1];
      // eslint-disable-next-line prefer-destructuring
      center.lng = sub_center.geometry.coordinates[0];
    }

    // Init new map
    initMap(
      refMap,
      refMapContainer,
      center,
      initZoom,
      mapStyle,
      false,
      {
        handleMapLoadCallback,
      },
      undefined,
      undefined,
      true,
    );

    refMap.current.on('wheel', (event) => {
      // Slightly inelegant but it works fairly well https://github.com/mapbox/mapbox-gl-js/issues/6884
      if (
        !(
          event.originalEvent.ctrlKey ||
          event.originalEvent.metaKey ||
          event.originalEvent.altKey
        )
      ) {
        event.preventDefault();
      }
    });
    refMap.current
      .getCanvas()
      .setAttribute('title', 'Press ctrl and scroll to zoom in and out');

    if (subscriptionBoundary) {
      fitBoundsToBbox(refMap.current, subscriptionBoundary.geometry, 0.1);
    }
    return unmount;
  }, [mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const clearup = () => {
      if (refMap.current) {
        mapLayerSafeRemove(refMap.current, kSubscriptionProjectLineLayerId);
        mapLayerSafeRemove(refMap.current, kSubscriptionProjectFillLayerId);
        mapLayerSafeRemove(refMap.current, kSubscriptionProjectLabelsLayerId);
        mapSourceSafeRemove(refMap.current, kSubscriptionProjectSourceId);
        mapSourceSafeRemove(refMap.current, kSubscriptionProjectLabelsSourceId);
      }
    };
    if (mapReady && refStyleReady.current) {
      if (subscription) {
        const boundaries: Feature<Polygon>[] = [];
        const centroids: Feature<Point>[] = [];
        const boundary_mapping = (proj: UserProject | UserProjectRequest) => {
          if (!proj.boundary) return undefined;
          return turf.feature(
            proj.boundary,
            {
              status: proj.status,
              name: proj.project_name.substring(0, 200),
              hash_id: (proj as any).hash_id,
              project_id: (proj as any).project_id,
            },
            {},
          );
        };
        const centroid_mapping = (proj: UserProject | UserProjectRequest) => {
          if (!proj.boundary) return undefined;
          return turf.centroid(proj.boundary, {
            properties: {
              status: proj.status,
              name: proj.project_name.substring(0, 200),
            },
          });
        };
        boundaries.push(...subscription.projects.map(boundary_mapping));
        boundaries.push(...subscription.project_requests.map(boundary_mapping));
        centroids.push(...subscription.projects.map(centroid_mapping));
        centroids.push(...subscription.project_requests.map(centroid_mapping));
        clearup();
        refMap.current.addSource(kSubscriptionProjectSourceId, {
          type: 'geojson',
          data: turf.featureCollection(boundaries),
        });

        refMap.current.addSource(kSubscriptionProjectLabelsSourceId, {
          type: 'geojson',
          data: turf.featureCollection(centroids),
        });

        addMapLayerInDefinedStack(
          refMap.current,
          getSubscriptionProjectsFillLayerDefinition(mapStyle),
        );
        addMapLayerInDefinedStack(
          refMap.current,
          getSubscriptionProjectsLineLayerDefinition(mapStyle),
        );
        addMapLayerInDefinedStack(
          refMap.current,
          getSubscriptionProjectsLabelLayerDefinition(mapStyle),
        );
        if (!subscription?.template?.region_boundary) {
          const boundaries_combined =
            boundaries.length <= 1
              ? boundaries[0]
              : turf.union(turf.featureCollection(boundaries));
          if (boundaries_combined) {
            fitBoundsToBbox(refMap.current, boundaries_combined.geometry, 0.1);
          }
        }

        refMap.current.on('click', kSubscriptionProjectFillLayerId, (ev) => {
          navigate(
            projectUrl(subscription.subscription_id, ev.features[0].properties),
          );
        });
      }
    }
    return clearup;
  }, [subscription, mapReady]);

  return (
    <div ref={refMapContainer} className={styles.boundary_map_container} />
  );
}

export function SubscriptionProjectsMap({
  subscription,
}: {
  subscription: UserProjectSubscription;
}) {
  return (
    <MapContextProvider>
      <SubscriptionProjectsMapInner subscription={subscription} />
    </MapContextProvider>
  );
}
